import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { HistoryItem } from '../api/types';

interface Props {
  /** Лента всего сообщества вместо своей. Только для админов. */
  community?: boolean;
}

function when(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Знак важнее цифры: начисление и списание должны различаться
 *  до чтения подписи. */
function money(amount: number | null, kind: string): string | null {
  if (amount === null) return null;
  const value = Math.abs(amount).toLocaleString('ru-RU');
  if (kind === 'bonus') return `${amount > 0 ? '+' : '−'}${value}`;
  return value;
}

export function HistoryPage({ community = false }: Props) {
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const load = community ? api.communityHistory() : api.history();
    load.then(setItems).catch(() => setFailed(true));
  }, [community]);

  const title = community ? 'Все операции' : 'История';

  if (failed) {
    return (
      <div className="screen">
        <h1 className="screen__title">{title}</h1>
        <p className="muted">Не удалось загрузить.</p>
      </div>
    );
  }

  if (!items) {
    return (
      <div className="screen">
        <h1 className="screen__title">{title}</h1>
        <div className="skeleton" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="screen">
        <h1 className="screen__title">{title}</h1>
        <p className="muted">
          {community
            ? 'Операций пока не было.'
            : 'Здесь появятся ваши записи, оплаты, бонусы и награды.'}
        </p>
      </div>
    );
  }

  return (
    <div className="screen">
      <h1 className="screen__title">{title}</h1>
      <p className="muted">
        {community
          ? 'Записи, оплаты, бонусы и награды по всему сообществу — одной лентой.'
          : 'Всё, что происходило: записи, оплаты, начисления и списания бонусов, награды.'}
      </p>

      <div className="feed">
        {items.map((item, index) => (
          <div key={`${item.kind}-${item.at}-${index}`} className="op">
            <div className="op__icon">{item.icon}</div>
            <div className="op__body">
              <div className="op__head">
                <span className="op__title">{item.title}</span>
                {money(item.amount_idr, item.kind) && (
                  <span
                    className={`op__sum${
                      item.kind === 'bonus' && (item.amount_idr ?? 0) > 0
                        ? ' op__sum--plus'
                        : ''
                    }`}
                  >
                    {money(item.amount_idr, item.kind)}
                  </span>
                )}
              </div>
              <div className="muted small">
                {when(item.at)}
                {community && item.who && ` · ${item.who}`}
                {item.subtitle && ` · ${item.subtitle}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
